import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingScreen from '../../../../ReusableBlocks/LoadingScreen';
import './SalesImpactAnalytics.css';

const buildEmbedStyle = (styleId) => `
<style id="${styleId}">
html,
body {
  margin: 0;
  overflow: hidden !important;
}
html {
  scrollbar-width: thin;
  scrollbar-color: #9ca3af transparent;
}
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 999px;
}
::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
</style>
`;

const buildEmbedResizerScript = (resizerId, messageType) => `
<script id="${resizerId}">
(function () {
  var MESSAGE_TYPE = '${messageType}';
  var DEBOUNCE_MS = 50;
  var debounceTimer = null;

  function isElementHidden(element) {
    var current = element;
    while (current && current !== document.body) {
      var style = window.getComputedStyle(current);
      if (
        style.display === 'none'
        || style.visibility === 'hidden'
        || style.opacity === '0'
        || current.hasAttribute('hidden')
        || current.getAttribute('aria-hidden') === 'true'
      ) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  function clipsOverflow(element) {
    var style = window.getComputedStyle(element);
    var overflowY = style.overflowY;
    var overflow = style.overflow;
    return overflowY === 'auto'
      || overflowY === 'scroll'
      || overflowY === 'hidden'
      || overflow === 'auto'
      || overflow === 'scroll'
      || overflow === 'hidden';
  }

  function isScrollViewport(element) {
    return clipsOverflow(element) && element.clientHeight < element.scrollHeight;
  }

  function getClippedBottom(element) {
    var rect = element.getBoundingClientRect();
    var bottom = rect.bottom;
    var parent = element.parentElement;

    while (parent && parent !== document.body) {
      if (isScrollViewport(parent)) {
        bottom = Math.min(bottom, parent.getBoundingClientRect().bottom);
        break;
      }
      parent = parent.parentElement;
    }

    return bottom;
  }

  function measureHeight() {
    var body = document.body;
    if (!body) {
      return 0;
    }

    var maxBottom = 0;
    var elements = body.getElementsByTagName('*');

    for (var index = 0; index < elements.length; index += 1) {
      var element = elements[index];
      if (isElementHidden(element)) {
        continue;
      }

      if (isScrollViewport(element)) {
        var viewportRect = element.getBoundingClientRect();
        if (viewportRect.height > 0) {
          maxBottom = Math.max(maxBottom, viewportRect.bottom);
        }
        continue;
      }

      var rect = element.getBoundingClientRect();
      if (rect.height > 0) {
        maxBottom = Math.max(maxBottom, getClippedBottom(element));
      }
    }

    if (maxBottom > 0) {
      return Math.ceil(maxBottom);
    }

    return Math.max(
      body.scrollHeight || 0,
      document.documentElement.scrollHeight || 0,
      body.offsetHeight || 0,
      document.documentElement.offsetHeight || 0,
    );
  }

  function reportHeight() {
    var height = measureHeight();
    if (height > 0 && window.parent) {
      window.parent.postMessage({ type: MESSAGE_TYPE, height: height }, '*');
    }
  }

  function scheduleReport() {
    if (debounceTimer) {
      window.clearTimeout(debounceTimer);
    }

    debounceTimer = window.setTimeout(function () {
      debounceTimer = null;
      reportHeight();
    }, DEBOUNCE_MS);
  }

  window.reportEmbedHeight = reportHeight;

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(scheduleReport).observe(document.documentElement);
  }

  if (typeof MutationObserver !== 'undefined' && document.body) {
    new MutationObserver(scheduleReport).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-selected'],
    });
  }

  document.addEventListener('click', scheduleReport, true);
  window.addEventListener('resize', scheduleReport);
  window.addEventListener('load', reportHeight);

  if (document.readyState === 'complete') {
    reportHeight();
  } else {
    document.addEventListener('DOMContentLoaded', reportHeight);
  }

  window.requestAnimationFrame(reportHeight);
})();
</script>
`;

const injectIntoHead = (html, snippet) => {
  if (html.includes('</head>')) {
    return html.replace('</head>', `${snippet}</head>`);
  }

  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${snippet}`);
  }

  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html([^>]*)>/i, `<html$1><head>${snippet}</head>`);
  }

  return `${snippet}${html}`;
};

const injectIntoBody = (html, snippet) => {
  if (html.includes('</body>')) {
    return html.replace('</body>', `${snippet}</body>`);
  }

  return `${html}${snippet}`;
};

const injectEmbedAssets = (html, styleId, resizerId, embedStyle, embedResizerScript) => {
  let result = html;

  if (!result.includes(styleId)) {
    result = injectIntoHead(result, embedStyle);
  }

  if (!result.includes(resizerId)) {
    result = injectIntoBody(result, embedResizerScript);
  }

  return result;
};

const ImpactAnalyticsEmbed = ({
  fetchHtml,
  embedHeightMessage,
  embedStyleId,
  embedResizerId,
  loadingText,
  iframeTitle,
  loadErrorFallback,
}) => {
  const accessToken = useSelector((state) => state.auth?.accessToken);
  const [iframeSrc, setIframeSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const blobUrlRef = useRef(null);
  const iframeRef = useRef(null);

  const embedStyle = useMemo(
    () => buildEmbedStyle(embedStyleId),
    [embedStyleId]
  );
  const embedResizerScript = useMemo(
    () => buildEmbedResizerScript(embedResizerId, embedHeightMessage),
    [embedResizerId, embedHeightMessage]
  );

  useEffect(() => {
    const handleEmbedHeightMessage = (event) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) {
        return;
      }

      if (event.data?.type !== embedHeightMessage) {
        return;
      }

      const height = Number(event.data.height);
      if (!Number.isFinite(height) || height <= 0) {
        return;
      }

      iframe.style.height = `${Math.ceil(height)}px`;
    };

    window.addEventListener('message', handleEmbedHeightMessage);
    return () => window.removeEventListener('message', handleEmbedHeightMessage);
  }, [embedHeightMessage]);

  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      setLoading(true);
      setError('');
      setIframeSrc('');

      try {
        const html = await fetchHtml(accessToken);
        if (cancelled) {
          return;
        }

        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }

        const preparedHtml = injectEmbedAssets(
          html,
          embedStyleId,
          embedResizerId,
          embedStyle,
          embedResizerScript
        );
        const blob = new Blob([preparedHtml], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setIframeSrc(url);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || loadErrorFallback);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (accessToken) {
      loadReport();
    } else {
      setLoading(false);
      setError('You must be signed in to view this report.');
    }

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [
    accessToken,
    embedResizerId,
    embedResizerScript,
    embedStyle,
    embedStyleId,
    fetchHtml,
    loadErrorFallback,
  ]);

  if (loading) {
    return <LoadingScreen text={loadingText} />;
  }

  if (error) {
    return (
      <div className="sales-impact-analytics-message" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="sales-impact-analytics-container">
      <iframe
        ref={iframeRef}
        title={iframeTitle}
        src={iframeSrc}
        className="sales-impact-analytics-iframe"
        loading="lazy"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
        allow="clipboard-write"
        scrolling="no"
      />
    </div>
  );
};

export default ImpactAnalyticsEmbed;
